<template>
    <div>
        <div
            class="tw-font-bold tw-text-lg tw-mb-4 tw-mt-16"
            data-test-id="invited-participants-headline"
        >
            {{ $t("surveys.period.invitesBySurveyPeriod") }}
        </div>

        <l-multi-filter
            class="tw-max-w-screen-md"
            :filter-types="filterTypes"
            :preselected-filters="preselectedFilters"
            :auto-apply-preselected-filters="true"
            @update="onFilterChange"
        />

        <div
            v-if="isAnonymousSurvey"
            class="tw-text-gray-400 tw-mt-2 tw-mb-4"
            data-test-id="invited-participants-anonymous-hint"
        >
            <i class="fal fa-info-circle tw-mr-2" />
            <span>{{ $t("surveys.period.anonymityHint") }}</span>
        </div>

        <l-skeleton-loader v-if="isLoading && offset === 0" />

        <div v-else-if="hasLoadingError" class="tw-text-gray-400 tw-text-center">
            {{ $t("surveys.period.errorWhileFetchingParticipants") }}
        </div>

        <div v-else-if="!hasParticipants" class="tw-text-gray-400 tw-text-center">
            {{ $t("surveys.period.noParticipants") }}
        </div>

        <div v-else>
            <l-infinite-scroll
                :list="participants"
                :on-list-bottom="fetchMoreParticipants"
                :attach-to-window-scroll="true"
                :can-load-more="!!canLoadMore"
            >
                <template #item="{ item: notification }">
                    <div
                        class="tw-flex tw-flex-col tw-space-y-5 xl:tw-flex-row xl:tw-space-x-8 xl:tw-space-y-0 tw-items-center tw-py-5 tw-pr-8 tw-border-0 tw-border-b tw-border-solid tw-border-gray-100 tw-bg-white"
                        data-test-class="invited-participant"
                    >
                        <a
                            :href="`#/profile/${notification.receiver._id}`"
                            target="_blank"
                            class="tw-text-gray-600 tw-w-72 tw-flex tw-items-center tw-space-x-4"
                            :title="notification.receiver.fullname"
                        >
                            <l-profile-picture size="m" :user="notification.receiver" />

                            <div class="tw-leading-none">
                                <div class="tw-line-clamp-1 tw-font-medium">
                                    {{ notification.receiver.fullname }}
                                </div>

                                <div class="tw-text-sm tw-text-gray-400 tw-mt-1">
                                    {{ notification.invitedAt | moment("MMM DD, YYYY - HH:mm") }}
                                </div>
                            </div>
                        </a>

                        <div class="tw-flex-1 tw-text-right">
                            <template v-if="notification.status === 'ANSWERED'">
                                <i
                                    v-b-popover.hover.top="$t('surveys.period.statusAnswered')"
                                    class="fal fa-check-circle fa-fw tw-text-green-500 tw-text-lg tw-mr-2"
                                    data-test-class="status-answered"
                                />
                            </template>
                            <template v-else-if="notification.status === 'DISMISSED'">
                                <i
                                    v-b-popover.hover.top="$t('surveys.period.statusDismissed')"
                                    class="fal fa-times-circle fa-fw tw-text-gray-400 tw-text-lg tw-mr-2"
                                    data-test-class="status-dismissed"
                                />
                            </template>
                            <template v-else-if="notification.status === 'EXPIRED'">
                                <i
                                    v-b-popover.hover.top="$t('surveys.period.statusExpired')"
                                    class="fal fa-clock fa-fw tw-text-gray-400 tw-text-lg tw-mr-2"
                                    data-test-class="status-expired"
                                />
                            </template>
                            <template v-else-if="notification.status === 'INVITED'">
                                <i
                                    v-b-popover.hover.top="$t('surveys.period.statusInvited')"
                                    class="fal fa-hourglass-half fa-fw tw-text-yellow-500 tw-text-lg tw-mr-2"
                                    data-test-class="status-invited"
                                />
                            </template>
                        </div>
                    </div>
                </template>
            </l-infinite-scroll>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LSkeletonLoader from "@/components/LSkeletonLoader";
import LMultiFilter from "@/components/LMultiFilter";
import LInfiniteScroll from "@/components/LInfiniteScroll";
import LProfilePicture from "@/components/LProfilePicture";

export default {
    components: {
        LSkeletonLoader,
        LMultiFilter,
        LInfiniteScroll,
        LProfilePicture,
    },
    props: {
        surveyId: {
            type: String,
            required: true,
        },
        surveyPeriods: {
            type: Array,
            default: () => [],
        },
        isAnonymousSurvey: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            hasLoadingError: false,
            isLoading: true,
            offset: 0,
            activeFilters: {},
        };
    },
    computed: {
        ...mapGetters({
            participants: "surveys/period/participants",
        }),
        canLoadMore() {
            return (
                this.participants?.length &&
                this.offset < this.participants.length &&
                !this.isLoading
            );
        },
        hasParticipants() {
            return Array.isArray(this.participants) && this.participants.length;
        },
        filterTypes() {
            return [
                {
                    value: "period",
                    key: "period",
                    text: this.$t(`surveys.details.filterByPeriod`),
                    mutuallyExclusiveOptions: true,
                    options: (this.surveyPeriods || []).map(p => ({
                        name: new Date(p.start).toDateString(),
                        _id: p.start,
                    })),
                },
                {
                    value: "user",
                    key: "user",
                    text: this.$t(`surveys.details.filterByUser`),
                },
            ];
        },
        latestPeriod() {
            return this.surveyPeriods?.[0];
        },
        preselectedFilters() {
            if (!this.latestPeriod) return {};

            return {
                period: [
                    {
                        name: new Date(this.latestPeriod.start).toDateString(),
                        _id: this.latestPeriod.start,
                    },
                ],
            };
        },
    },
    beforeDestroy() {
        this.$store.commit("surveys/period/resetModuleState");
    },
    methods: {
        async loadData({ surveryPeriodStartDate, users, surveyId, offset } = {}) {
            if (!surveyId) return;

            this.isLoading = true;
            this.hasLoadingError = false;

            try {
                await this.$store.dispatch("surveys/period/getParticipants", {
                    payload: { surveyId, surveryPeriodStartDate, users, offset },
                });
            } catch {
                this.hasLoadingError = true;
            } finally {
                this.isLoading = false;
            }
        },
        fetchMoreParticipants() {
            if (!this.canLoadMore) return;
            this.offset = this.participants.length;
            this.onFilterChange({ ...this.activeFilters, offset: this.offset });
        },
        onFilterChange(filters = {}) {
            if (!filters.offset) this.offset = 0;
            this.activeFilters = filters;

            const relevantUsers = filters.user?.map(user => user._id) ?? [];
            let relevantPeriod = filters.period?.map(period => period._id)?.[0];

            // enforced default
            if (!relevantPeriod) {
                relevantPeriod = this.latestPeriod?.start;
            }

            this.loadData({
                surveryPeriodStartDate: relevantPeriod,
                users: relevantUsers,
                surveyId: this.surveyId,
                offset: this.offset,
            });
        },
    },
};
</script>
